import * as React from 'react';
import type { SVGProps } from 'react';
import OtIcon from './OtIcon';

/**
 * Illustrated family — objects that only appear on empty states and gated surfaces.
 * Lighting model and construction rules live in {@link OtIcon}.
 */

type TProps = SVGProps<SVGSVGElement>;

const Spec = ({ d, o = 0.92, w = 2.6 }: { d: string; o?: number; w?: number }) => (
	<path d={d} stroke='#fff' strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

const Bounce = ({ d, c, o = 0.5, w = 2 }: { d: string; c: string; o?: number; w?: number }) => (
	<path d={d} stroke={c} strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

/* ------------------------------------------------------------------ Campaign */
/** A megaphone: the surface where something gets published or announced. */
export const OtCampaign = (props: TProps) => (
	<OtIcon slug='campaign' palette='orange' groundX={46} groundY={80} groundR={24} {...props}>
		{(r) => (
			<>
				{/* sound, drawn before the horn so the horn occludes its root */}
				<path
					d='M78 28a26 26 0 0 1 0 30M86 20a38 38 0 0 1 0 46'
					fill='none'
					stroke={r.tone.bounce}
					strokeOpacity='.55'
					strokeWidth='3.4'
					strokeLinecap='round'
				/>
				{/* horn */}
				<path d='M66 12v62L28 58V28z' fill={r.fill} />
				<path
					d='M66 12v62L28 58V28z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
					strokeLinejoin='round'
				/>
				{/* barrel */}
				<rect x='10' y='28' width='20' height='30' rx='7' fill={r.fill} />
				<rect
					x='10'
					y='28'
					width='20'
					height='30'
					rx='7'
					fill='none'
					stroke={r.rim}
					strokeWidth='3'
				/>
				<path
					d='M28 28v30'
					stroke={r.tone.occlusion}
					strokeOpacity='.45'
					strokeWidth='2.2'
				/>
				{/* handle */}
				<rect x='34' y='58' width='11' height='20' rx='5' fill={r.body} fillOpacity='.9' />
				<Spec d='M16 34a6 6 0 0 1 5-4' o={0.9} w={2.4} />
				<Bounce d='M60 66v-8' c='#ffd7a0' o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Inventory */
/** A box: an archive, a store of things already put away. */
export const OtInventory = (props: TProps) => (
	<OtIcon slug='inventory' palette='orange' groundX={47} groundY={80} groundR={26} {...props}>
		{(r) => (
			<>
				{/* body */}
				<path d='M14 30h66v34a10 10 0 0 1-10 10H24a10 10 0 0 1-10-10z' fill={r.fill} />
				<path
					d='M14 30h66v34a10 10 0 0 1-10 10H24a10 10 0 0 1-10-10z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
					strokeLinejoin='round'
				/>
				{/* lid, sitting proud of the body and casting onto it */}
				<ellipse
					cx='47'
					cy='31'
					rx='33'
					ry='3'
					fill={r.tone.occlusion}
					opacity='.5'
					filter={r.tight}
				/>
				<rect x='10' y='14' width='74' height='18' rx='7' fill={r.fill} />
				<rect
					x='10'
					y='14'
					width='74'
					height='18'
					rx='7'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
				/>
				{/* the slot in the front face */}
				<rect x='36' y='42' width='22' height='7' rx='3.5' fill={r.tone.occlusion} fillOpacity='.6' />
				<rect x='36' y='41' width='22' height='6' rx='3' fill={r.body} fillOpacity='.85' />
				<Spec d='M17 22a7 7 0 0 1 7-5.4h12' o={0.9} w={2.4} />
				<Bounce d='M76 60a9 9 0 0 1-6 7' c='#7fe6d2' o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Lock */
/** A padlock: the mark of a gated surface — a feature the plan does not include yet. */
export const OtLock = (props: TProps) => (
	<OtIcon slug='lock' palette='gold' groundX={46} groundY={81} groundR={22} {...props}>
		{(r) => (
			<>
				{/* shackle, behind the body */}
				<path
					d='M30 38V28a16 16 0 0 1 32 0v10'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.55'
					strokeWidth='11'
					strokeLinecap='round'
				/>
				<path
					d='M30 38V28a16 16 0 0 1 32 0v10'
					fill='none'
					stroke={r.rim}
					strokeWidth='7.5'
					strokeLinecap='round'
				/>
				<path
					d='M28.6 36.6V28a16 16 0 0 1 9-14.4'
					fill='none'
					stroke='#fff'
					strokeOpacity='.6'
					strokeWidth='2.2'
					strokeLinecap='round'
				/>
				{/* body */}
				<rect x='16' y='36' width='60' height='40' rx='12' fill={r.fill} />
				<rect
					x='16'
					y='36'
					width='60'
					height='40'
					rx='12'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
				/>
				<rect
					x='18.7'
					y='38.7'
					width='54.6'
					height='34.6'
					rx='9.8'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.4'
					strokeWidth='1.4'
				/>
				{/* keyhole, sunk into the face */}
				<circle cx='46' cy='52' r='6' fill={r.tone.occlusion} fillOpacity='.75' />
				<path d='M43 56h6l2 10H41z' fill={r.tone.occlusion} fillOpacity='.75' />
				<circle cx='45.2' cy='51.2' r='4.2' fill={r.body} fillOpacity='.55' />
				<Spec d='M23 46a10 10 0 0 1 9-7' o={0.9} w={2.4} />
				<Bounce d='M71 62a10 10 0 0 1-6 8' c='#ffd7a0' o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Upload */
/** A sheet leaving the tray: importing a CV, a list, a file the customer already has. */
export const OtUpload = (props: TProps) => (
	<OtIcon slug='upload' palette='green' groundX={47} groundY={81} groundR={24} {...props}>
		{(r) => (
			<>
				{/* the sheet, lifted — its shadow on the tray is what says "leaving" */}
				<rect x='31' y='8' width='32' height='40' rx='8' fill={r.body} />
				<rect
					x='31'
					y='8'
					width='32'
					height='40'
					rx='8'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.8'
				/>
				<path
					d='M47 40V20M39 27l8-8 8 8'
					fill='none'
					stroke={r.tone.dark}
					strokeOpacity='.75'
					strokeWidth='4.4'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
				<ellipse
					cx='47'
					cy='52'
					rx='19'
					ry='4'
					fill={r.tone.occlusion}
					opacity='.55'
					filter={r.tight}
				/>
				{/* tray */}
				<path d='M14 44h18l5 10h20l5-10h18v20a10 10 0 0 1-10 10H24a10 10 0 0 1-10-10z' fill={r.fill} />
				<path
					d='M14 44h18l5 10h20l5-10h18v20a10 10 0 0 1-10 10H24a10 10 0 0 1-10-10z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
					strokeLinejoin='round'
				/>
				<Spec d='M19 52a9 9 0 0 1 8-6' o={0.85} w={2.4} />
				<Bounce d='M76 62a9 9 0 0 1-6 7' c={r.tone.bounce} o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Public */
/** A globe: everything public, open or reachable from outside the organization. */
export const OtPublic = (props: TProps) => (
	<OtIcon
		slug='public'
		palette='cyan'
		groundX={46}
		groundY={80}
		groundR={22}
		extraDefs={(refs, id) => (
			<radialGradient id={id('sphere')} gradientUnits='userSpaceOnUse' cx='34' cy='28' r='48'>
				<stop offset='0' stopColor='#93aeff' />
				<stop offset='.5' stopColor='#4d69fa' />
				<stop offset='1' stopColor='#141d5c' />
			</radialGradient>
		)}
		{...props}>
		{(r) => (
			<>
				<circle cx='46' cy='42' r='32' fill={r.own('sphere')} />
				{/* meridians and parallels, clipped by the sphere's own edge */}
				<ellipse
					cx='46'
					cy='42'
					rx='13'
					ry='32'
					fill='none'
					stroke='#fff'
					strokeOpacity='.42'
					strokeWidth='2.2'
				/>
				<path
					d='M17 30h58M17 54h58M14 42h64'
					stroke='#fff'
					strokeOpacity='.34'
					strokeWidth='2.2'
					strokeLinecap='round'
				/>
				<circle cx='46' cy='42' r='32' fill='none' stroke={r.rim} strokeWidth='3.4' />
				<circle
					cx='46'
					cy='42'
					r='29.4'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.4'
					strokeWidth='1.6'
				/>
				<Spec d='M26 27a30 30 0 0 1 13-8.6' o={0.85} />
				<Bounce d='M67 60a30 30 0 0 1-11 8' c='#7fe6d2' o={0.55} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Video */
/**
 * A play button. The first version drew a screen, which at 24px is a rectangle — the same box every
 * other panel-shaped icon was reading as. A disc with a triangle cannot be mistaken for a container.
 */
export const OtVideo = (props: TProps) => (
	<OtIcon slug='video' palette='pink' groundX={46} groundY={80} groundR={22} {...props}>
		{(r) => (
			<>
				<circle cx='46' cy='42' r='32' fill={r.fill} />
				<circle cx='46' cy='42' r='32' fill='none' stroke={r.rim} strokeWidth='3.6' />
				<circle
					cx='46'
					cy='42'
					r='29.2'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.42'
					strokeWidth='1.6'
				/>
				{/* the triangle sits proud of the disc, so it casts onto it */}
				<path d='M39 28l24 14-24 14z' fill={r.tone.occlusion} fillOpacity='.5' />
				<path d='M37.6 26.6l24 14-24 14z' fill={r.body} />
				<Spec d='M27 28a30 30 0 0 1 13-9' o={0.85} />
				<Bounce d='M67 60a30 30 0 0 1-11 8' c={r.tone.bounce} o={0.55} />
			</>
		)}
	</OtIcon>
);
